import { useMediaQuery, useTheme } from '@mui/material';
import { useMemo } from 'react';
import type { ColumnDef, ColumnVisibilityOptions } from '../types';

export const useColumnVisibility = <T>(
  columns: readonly ColumnDef<T>[],
  options: ColumnVisibilityOptions = {},
) => {
  const theme = useTheme();
  const isXLarge = useMediaQuery(theme.breakpoints.down('xl')); // 1920px
  const isMedium = useMediaQuery(theme.breakpoints.down('md')); // 900px
  const isSmall = useMediaQuery(theme.breakpoints.down('sm')); // 768px

  return useMemo(() => {
    const visibleColumns = columns.filter((col) => {
      // Handle explicit options
      if (col.key === 'actions' && options.hideActions) return false;
      if (col.key === 'check' && options.hideCheckbox) return false;
      if (col.key === 'handle' && (options.hideDragHandle || isSmall)) return false;

      // Handle responsive breakpoints
      if (col.responsive === 'sm-up' && isSmall) return false;
      if (col.responsive === 'md-up' && isMedium) return false;
      if (col.responsive === 'xl-up' && isXLarge) return false;

      return true;
    });

    const gridTemplateColumns = visibleColumns
      .map((col) => (col.width === 'auto' ? '1fr' : `${col.width}px`))
      .join(' ');

    return {
      visibleColumns,
      gridTemplateColumns,
    };
  }, [
    columns,
    options.hideActions,
    options.hideCheckbox,
    options.hideDragHandle,
    isSmall,
    isMedium,
    isXLarge,
  ]);
};
